import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Bolt,
  Boxes,
  Brain,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Handshake,
  Layers3,
  LineChart,
  Package,
  ShieldCheck,
  UserCog,
  Wallet
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getDemoProducts, getLogoutRedirectPath, isDemoUser } from '../../services/demoSession'

const navigationItems = [
  { id: 'como-funciona', label: 'Cómo funciona' },
  { id: 'productos', label: 'Productos' },
  { id: 'casos', label: 'Casos de uso' },
  { id: 'arquitectura', label: 'Arquitectura' },
  { id: 'faq', label: 'FAQ' }
]

const capabilities = [
  { icon: Building2, title: 'Control de negocio', text: 'Operación centralizada con decisiones basadas en datos.' },
  { icon: Package, title: 'Inventario', text: 'Entradas, salidas y alertas de stock en un solo flujo.' },
  { icon: CalendarClock, title: 'Agendas', text: 'Citas, reservas y recordatorios automatizados.' },
  { icon: Boxes, title: 'Arriendos', text: 'Contratos, pagos y estados de propiedades en tiempo real.' },
  { icon: UserCog, title: 'Gestión de clientes', text: 'Seguimiento comercial y relación postventa unificada.' },
  { icon: Bolt, title: 'Automatización', text: 'Menos tareas manuales, más foco en crecimiento.' },
  { icon: Wallet, title: 'Pagos', text: 'Cobranza estructurada y trazabilidad de recaudo.' },
  { icon: LineChart, title: 'Reportes', text: 'KPIs claros para dirección estratégica.' }
]

const productCarouselItems = [
  {
    name: 'ROI Automatizacion',
    subtitle: 'Completamente gratis',
    description: 'Calcula tu ROI en minutos y valida si automatizar realmente paga. Estima ahorro, payback y retorno antes de invertir.',
    href: '/latam/soluciones/automatizacion-procesos/roi-automatizacion',
    gradientClass: 'from-emerald-500/10 to-cyan-500/10',
    icon: Brain
  },
  {
    name: 'AgendaLab',
    subtitle: 'Gestión de citas y servicios',
    description: 'Convierte la agenda en un motor de crecimiento: visibilidad de ocupación, control del servicio y seguimiento de clientes para tomar decisiones comerciales con ritmo y consistencia.',
    href: '/latam/soluciones/gestion-citas/agendalab',
    gradientClass: 'from-cyan-500/10 to-emerald-500/10',
    icon: CalendarClock
  },
  {
    name: 'Rental Biz',
    subtitle: 'Alquiler de inmuebles',
    description: 'Gestiona tu portafolio inmobiliario con visión gerencial: ocupación, cartera y flujo de caja en un mismo tablero para escalar con orden, previsibilidad y rentabilidad sostenible.',
    href: '/latam/soluciones/alquiler-inmuebles/rentalbiz',
    gradientClass: 'from-blue-500/10 to-cyan-500/10',
    icon: Building2
  },
  {
    name: 'Rental Tech',
    subtitle: 'Renta de maquinaria',
    description: 'Lidera la operación de maquinaria con control total: disponibilidad, mantenimiento y rentabilidad por activo para ejecutar más contratos con menor riesgo y mayor margen.',
    href: '/latam/soluciones/renta-maquinaria/rentaltech',
    gradientClass: 'from-sky-500/10 to-indigo-500/10',
    icon: Gauge
  }
]

const faqs = [
  {
    q: 'No sé programar, ¿igual me sirve?',
    a: 'Sí. AppEmprendedor está diseñado para emprendedores y microempresarios.'
  },
  {
    q: '¿Cuánto tarda tener algo funcionando?',
    a: 'Trabajamos por fases de ejecución acelerada. El objetivo es mostrar entregables funcionales en días, no meses.'
  },
  {
    q: '¿Necesito contratar un equipo de desarrollo?',
    a: 'No. Operamos como aliado tecnológico estratégico y nos encargamos de la implementación.'
  },
  {
    q: '¿Sirve para negocios físicos en LATAM?',
    a: 'Sí. Fue planteado para operaciones reales: retail, servicios, arriendos, agendas y control financiero.'
  },
  {
    q: '¿Puedo empezar pequeño y escalar después?',
    a: 'Sí. La arquitectura modular permite iniciar con lo crítico y crecer sin rehacer tu base.'
  }
]

const initialLead = {
  name: '',
  email: '',
  company: '',
  phone: '',
  interest: '',
  message: '',
  website: ''
}

const TURNSTILE_SITE_KEY = (() => {
  if (import.meta.env.VITE_TURNSTILE_SITE_KEY) {
    return import.meta.env.VITE_TURNSTILE_SITE_KEY
  }

  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '0x4AAAAAACewzlyKjy6eQokF'
  }

  return '0x4AAAAAACewdq3FV9qGlLZJ'
})()

const upsertMeta = (attribute, key, content) => {
  if (!content) return
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

const upsertCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', href)
}

const upsertJsonLd = (json) => {
  const id = 'home-jsonld'
  let script = document.getElementById(id)
  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(json)
}

export default function Home() {
  const { user, isAuthenticated, loading, logout } = useAuth()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [mobileMenu, setMobileMenu] = useState(false)
  const [activeProductSlide, setActiveProductSlide] = useState(0)
  const [lead, setLead] = useState(initialLead)
  const [leadState, setLeadState] = useState({ type: '', message: '' })
  const [leadSending, setLeadSending] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileContainerRef = useRef(null)
  const turnstileWidgetIdRef = useRef(null)

  useEffect(() => {
    if (!loading && isAuthenticated) {
      fetchProducts()
    }
  }, [isAuthenticated, loading])

  useEffect(() => {
    const canonicalUrl = `${window.location.origin}/`
    const title = 'AppEmprendedor | SaaS Factory y Calculadora ROI de Automatizacion Gratis'
    const description = 'AppEmprendedor impulsa negocios con software SaaS, automatizacion y una calculadora ROI de automatizacion completamente gratis para validar ahorro, payback y retorno.'

    document.title = title
    upsertCanonical(canonicalUrl)
    upsertMeta('name', 'description', description)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'AppEmprendedor',
      url: canonicalUrl,
      description,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${window.location.origin}/?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      },
      publisher: {
        '@type': 'Organization',
        name: 'AppEmprendedor'
      }
    })
  }, [])

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || isAuthenticated) {
      return undefined
    }

    let disposed = false

    const renderWidget = () => {
      if (disposed || !turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current !== null) {
        return
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken('')
      })
    }

    if (window.turnstile) {
      renderWidget()
      return () => { disposed = true }
    }

    let script = document.querySelector('script[data-turnstile="true"]')
    if (!script) {
      script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.setAttribute('data-turnstile', 'true')
      document.head.appendChild(script)
    }

    script.addEventListener('load', renderWidget)

    return () => {
      disposed = true
      script.removeEventListener('load', renderWidget)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      setActiveProductSlide((previous) => (previous + 1) % productCarouselItems.length)
    }, 6000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [isAuthenticated])

  const heroMetrics = useMemo(() => ([
    { value: 'Días', label: 'Primeros entregables funcionales' },
    { value: '1 Base', label: 'Infraestructura para múltiples módulos' },
    { value: '0 Equipo', label: 'Necesidad de contratar desarrollo interno' }
  ]), [])

  const fetchProducts = async () => {
    if (isDemoUser(user)) {
      setProducts(getDemoProducts())
      setLoadingProducts(false)
      return
    }

    try {
      const response = await fetch('/api/user/products', { credentials: 'include' })
      if (response.ok) {
        const data = await response.json()
        setProducts(data)
      }
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setLoadingProducts(false)
    }
  }

  const handleLogout = async () => {
    const redirectPath = getLogoutRedirectPath(user, window.location.pathname)
    await logout()
    window.location.href = redirectPath
  }

  const getProductRoute = (product) => {
    const productRoutes = {
      rentalbiz: '/rentalbiz/dashboard',
      appointments: '/agendalab',
      agendalab: '/agendalab',
      'brain-emprendedor': '/brain-emprendedor/dashboard'
    }
    return productRoutes[product.slug] || `/products/${product.slug}`
  }

  const handleProductClick = (product) => {
    navigate(getProductRoute(product))
  }

  const goToNextProductSlide = () => {
    setActiveProductSlide((previous) => (previous + 1) % productCarouselItems.length)
  }

  const goToPreviousProductSlide = () => {
    setActiveProductSlide((previous) => (previous - 1 + productCarouselItems.length) % productCarouselItems.length)
  }

  const normalizedRoles = (user?.Roles || user?.roles || [])
    .map((role) => String(role || '').trim().toLowerCase())
  const canAccessAdminModule = normalizedRoles.includes('admin') || normalizedRoles.includes('superadmin')

  const updateLead = (field, value) => {
    setLead((prev) => ({ ...prev, [field]: value }))
    if (leadState.message) {
      setLeadState({ type: '', message: '' })
    }
  }

  const submitLead = async (event) => {
    event.preventDefault()
    setLeadSending(true)
    setLeadState({ type: '', message: '' })

    if (!turnstileToken) {
      setLeadState({
        type: 'error',
        message: 'Completa la verificación anti-spam antes de enviar.'
      })
      setLeadSending(false)
      return
    }

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...lead,
          turnstileToken
        })
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo enviar la solicitud.')
      }

      setLead(initialLead)
      setTurnstileToken('')
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current)
      }
      setLeadState({
        type: 'success',
        message: payload?.message || 'Solicitud recibida. Te contactaremos en menos de 24 h hábiles.'
      })
    } catch (error) {
      setLeadState({
        type: 'error',
        message: error?.message || 'No pudimos procesar tu solicitud en este momento.'
      })
    } finally {
      setLeadSending(false)
    }
  }

  if (isAuthenticated && !loadingProducts) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-primary-600">AppEmprendedor</h1>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Hola, {user?.firstName || user?.email}</span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Mis Productos</h2>
          {canAccessAdminModule && (
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate('/admin/licenses')}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Ir al Módulo Administrativo
              </button>
            </div>
          )}
          {products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No tienes productos asignados.</p>
              <p className="text-gray-400 mt-2">Contacta al administrador para habilitar accesos.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {canAccessAdminModule && (
                <div
                  onClick={() => navigate('/admin/licenses')}
                  className="block bg-primary-50 border border-primary-200 rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden cursor-pointer"
                >
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">A</span>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-primary-900">Módulo Administrativo</h3>
                        <p className="text-sm text-primary-700">Gestión</p>
                      </div>
                    </div>
                    <p className="text-primary-800 text-sm">
                      Accede a licencias, usuarios, schema y configuración administrativa.
                    </p>
                  </div>
                </div>
              )}
              {products.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="block bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden cursor-pointer"
                >
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      {product.iconUrl ? (
                        <img src={product.iconUrl} alt={product.name} className="w-12 h-12 rounded-lg" />
                      ) : (
                        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                          <span className="text-2xl font-bold text-primary-600">{product.name.charAt(0)}</span>
                        </div>
                      )}
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-500">{product.category?.name || 'Sin categoría'}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm">{product.description || 'Sin descripción disponible'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    )
  }

  return (
    <div className="bg-slate-950 text-slate-100">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-180px] left-[-100px] h-[480px] w-[480px] rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute top-[180px] right-[-160px] h-[560px] w-[560px] rounded-full bg-blue-700/30 blur-3xl" />
        <div className="absolute bottom-[-200px] left-[25%] h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-3xl" />
      </div>

      <nav className="sticky top-0 z-40 border-b border-slate-800/70 backdrop-blur-xl bg-slate-950/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-cyan-400 shadow-[0_0_24px_rgba(34,211,238,0.95)]" />
            <span className="font-display text-xl tracking-tight">AppEmprendedor</span>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {navigationItems.map((item) => (
              <a key={item.id} href={`#${item.id}`} className="text-sm text-slate-300 hover:text-cyan-300 transition-colors">
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a href="/login" className="px-4 py-2 rounded-xl text-sm font-medium border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10 transition-colors">
              Acceder a la suite de apps
            </a>
            <a href="#cta" className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 transition-all">
              Solicita la revisión de tu idea
            </a>
          </div>

          <button
            type="button"
            className="lg:hidden p-2 rounded-lg border border-slate-700"
            onClick={() => setMobileMenu((prev) => !prev)}
            aria-label="Menu"
          >
            <ChevronDown className={`w-5 h-5 transition-transform ${mobileMenu ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {mobileMenu && (
          <div className="lg:hidden border-t border-slate-800 px-4 pb-4 space-y-3">
            {navigationItems.map((item) => (
              <a key={item.id} href={`#${item.id}`} className="block text-sm text-slate-300 py-1">
                {item.label}
              </a>
            ))}
            <a href="/api/auth/demo-entry?product=rentalbiz" className="block text-sm py-2">Ver demo</a>
            <a href="/login" className="block text-sm py-2">Acceder a la suite de apps</a>
            <a href="#cta" className="block text-sm py-2 font-semibold text-cyan-300">Solicita la revisión de tu idea</a>
          </div>
        )}
      </nav>

      <main>
        <section id="hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 text-cyan-200 text-xs font-semibold tracking-wide uppercase">
                <ShieldCheck className="w-3.5 h-3.5" />
                SaaS Factory para emprendedores de LATAM
              </span>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
                Tu idea merece ejecución. <span className="text-cyan-300">No meses de espera.</span>
              </h1>
              <p className="mt-6 text-slate-300 text-lg max-w-2xl">
                AppEmprendedor convierte ideas y operaciones manuales en sistemas SaaS funcionando sobre una infraestructura robusta.
                Sin equipo técnico interno. Sin complejidad innecesaria. Con foco total en resultado.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#cta" className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-950 hover:from-cyan-400 hover:to-blue-400 transition-all">
                  Solicita la revisión de tu idea
                </a>
                <a href="/login" className="px-6 py-3 rounded-xl font-semibold border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10 transition-colors">
                  Acceder a la suite de apps
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-cyan-500/30 to-blue-500/30 blur-xl" />
              <div className="relative rounded-3xl border border-slate-700 bg-slate-900/80 p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-cyan-100">Ventaja competitiva inmediata</h3>
                  <Gauge className="w-5 h-5 text-cyan-300" />
                </div>
                <div className="space-y-3 text-sm text-slate-300">
                  <p className="p-3 rounded-xl border border-slate-700 bg-slate-800/70">No vendemos teoría. Entregamos software operando.</p>
                  <p className="p-3 rounded-xl border border-slate-700 bg-slate-800/70">Una sola infraestructura para control, ventas y operación.</p>
                  <p className="p-3 rounded-xl border border-slate-700 bg-slate-800/70">Arquitectura modular: inicias rápido y escalas sin rehacer.</p>
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {heroMetrics.map((metric) => (
                    <div key={metric.label} className="rounded-xl border border-slate-700 bg-slate-800/70 p-3">
                      <div className="text-cyan-300 font-semibold">{metric.value}</div>
                      <div className="text-xs text-slate-400 mt-1">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="productos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-white">Carrusel de productos</h2>
            <p className="mt-4 text-slate-300">
              Explora nuestras soluciones y conoce la propuesta de valor de cada producto.
            </p>
          </div>

          <div className="relative mt-10">
            <div className="overflow-hidden rounded-3xl border border-slate-700 bg-slate-900/70">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${activeProductSlide * 100}%)` }}
              >
                {productCarouselItems.map((item) => {
                  const Icon = item.icon
                  const isRoiCard = item.name === 'ROI Automatizacion'
                  return (
                    <article key={item.name} className="min-w-full p-6 sm:p-8 lg:p-10 min-h-[380px] sm:min-h-[430px]">
                      <div className={`h-full rounded-2xl border border-slate-700 bg-gradient-to-br ${item.gradientClass} p-6 lg:p-8`}>
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                          <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-300/30 text-cyan-200 text-xs uppercase tracking-wide">
                              <Icon className="w-4 h-4" />
                              {item.subtitle}
                            </div>
                            <h3 className="mt-4 text-3xl font-display text-white">{item.name}</h3>
                            <p className="mt-3 text-slate-300 max-w-2xl">{item.description}</p>
                          </div>
                          <a
                            href={item.href}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold bg-cyan-400 text-slate-950 hover:bg-cyan-300 transition-colors whitespace-nowrap"
                          >
                            {isRoiCard ? 'Probar gratis ahora' : 'Ver página'}
                            <ArrowRight className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </article>
                  )
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={goToPreviousProductSlide}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-slate-600 bg-slate-900/90 text-slate-100 hover:border-cyan-300"
              aria-label="Producto anterior"
            >
              <ChevronLeft className="w-5 h-5 mx-auto" />
            </button>
            <button
              type="button"
              onClick={goToNextProductSlide}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-slate-600 bg-slate-900/90 text-slate-100 hover:border-cyan-300"
              aria-label="Siguiente producto"
            >
              <ChevronRight className="w-5 h-5 mx-auto" />
            </button>
          </div>

          <div className="mt-5 flex items-center justify-center gap-2">
            {productCarouselItems.map((item, index) => (
              <button
                key={item.name}
                type="button"
                onClick={() => setActiveProductSlide(index)}
                aria-label={`Ir a ${item.name}`}
                className={`h-2.5 rounded-full transition-all ${
                  activeProductSlide === index ? 'w-8 bg-cyan-300' : 'w-2.5 bg-slate-600 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>
        </section>

        <section id="problema" className="border-y border-slate-800 bg-slate-900/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-white">Tu negocio crece, pero tu operación se queda atrás.</h2>
              <p className="mt-5 text-slate-300">
                Cuando todo depende de hojas de cálculo, chat y procesos manuales, el crecimiento se convierte en desgaste.
                El problema no es tu idea: es operar sin infraestructura.
              </p>
              <a href="#cta" className="inline-flex items-center gap-2 mt-6 text-cyan-300 font-semibold">
                Solicita la revisión de tu idea <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="grid gap-3">
              {[
                'Inventario sin trazabilidad',
                'Cobros y agenda desconectados',
                'Decisiones sin datos consolidados',
                'Tiempo perdido en tareas repetitivas'
              ].map((item) => (
                <div key={item} className="rounded-xl border border-slate-700 bg-slate-800/70 p-4 text-slate-200 flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="cambio" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-white">Cambio de paradigma: de operar artesanalmente a operar con poder.</h2>
            <p className="mt-5 text-slate-300">
              Antes necesitabas meses y un equipo técnico completo. Hoy puedes ejecutar por fases en días, con IA aplicada
              para automatizar procesos y acelerar resultado.
            </p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-red-400/20 bg-red-500/5 p-6">
              <h3 className="text-red-200 font-semibold">Antes</h3>
              <ul className="mt-4 space-y-2 text-slate-300 text-sm">
                <li>Desarrollo lento y costoso</li>
                <li>Herramientas aisladas sin integración</li>
                <li>Dependencia total de terceros</li>
                <li>Poca visibilidad para decidir</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-cyan-400/25 bg-cyan-500/5 p-6">
              <h3 className="text-cyan-200 font-semibold">Ahora con AppEmprendedor + IA</h3>
              <ul className="mt-4 space-y-2 text-slate-300 text-sm">
                <li>Implementación por fases en días</li>
                <li>Base única para todos tus procesos</li>
                <li>Automatización operativa real</li>
                <li>Control desde panel en tiempo real</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="como-funciona" className="border-y border-slate-800 bg-slate-900/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-white">AppEmprendedor es tu aliado tecnológico estratégico.</h2>
              <p className="mt-5 text-slate-300">
                Somos la SaaS Factory definitiva para emprendedores modernos. Construimos soluciones funcionando, con una
                arquitectura escalable y orientada a negocio.
              </p>
              <div className="mt-6 space-y-3 text-sm text-slate-300">
                <p className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Ejecución real, no consultoría vacía.</p>
                <p className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Velocidad de implementación sin sacrificar estructura.</p>
                <p className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Escalabilidad técnica para crecer sin rehacer.</p>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 p-6">
              <h3 className="text-cyan-100 font-semibold">Cómo funciona en 3 pasos</h3>
              <div className="mt-5 space-y-4">
                {[
                  ['1', 'Revisión estratégica', 'Priorizamos el proceso crítico que más impacto genera.'],
                  ['2', 'Implementación acelerada', 'Activamos módulos y flujo operativo sobre infraestructura base.'],
                  ['3', 'Operación y escalamiento', 'Mides, optimizas y amplías sin romper lo existente.']
                ].map(([n, t, d]) => (
                  <div key={n} className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <p className="text-cyan-300 font-semibold">{n}. {t}</p>
                    <p className="text-sm text-slate-300 mt-1">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="casos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-display text-3xl sm:text-4xl text-white text-center">Una infraestructura. Múltiples soluciones.</h2>
          <p className="mt-4 text-center text-slate-300 max-w-3xl mx-auto">
            Resuelve operaciones reales desde un solo ecosistema: control, clientes, pagos, agenda, inventario y reportes.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-2xl border border-slate-700 bg-slate-900/70 p-5 hover:border-cyan-400/40 transition-colors">
                <Icon className="w-5 h-5 text-cyan-300" />
                <h3 className="mt-3 font-semibold text-slate-100">{title}</h3>
                <p className="mt-2 text-sm text-slate-400">{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="arquitectura" className="border-y border-slate-800 bg-slate-900/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-white">Arquitectura inteligente para crecer sin fricción.</h2>
              <p className="mt-5 text-slate-300">
                Empiezas con lo esencial y habilitas nuevos módulos cuando el negocio lo requiere. Todo conectado sobre una
                base central segura y escalable.
              </p>
            </div>
            <div className="grid gap-3 text-sm">
              {[
                ['Base central de datos', 'Una sola fuente de verdad para toda la operación.'],
                ['Módulos desacoplados', 'Escalas por necesidad sin afectar lo ya construido.'],
                ['Seguridad por roles', 'Permisos y acceso controlado para cada perfil.'],
                ['Panel ejecutivo', 'Control integral desde una vista clara.']
              ].map(([title, desc]) => (
                <div key={title} className="rounded-xl border border-slate-700 bg-slate-800/70 p-4">
                  <p className="font-semibold text-slate-100 flex items-center gap-2"><Layers3 className="w-4 h-4 text-cyan-300" />{title}</p>
                  <p className="text-slate-400 mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimonio" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="rounded-3xl border border-slate-700 bg-gradient-to-r from-slate-900 to-slate-800 p-8 lg:p-10">
            <p className="text-cyan-300 uppercase text-xs font-semibold tracking-wider">Argumento estratégico</p>
            <blockquote className="mt-4 text-2xl font-display text-white leading-relaxed">
              Cuando una operación pasa de mensajes y hojas sueltas a un sistema centralizado, el negocio gana control,
              velocidad y capacidad real de escalar sin depender de improvisación.
            </blockquote>
            <p className="mt-5 text-slate-300">AppEmprendedor · SaaS Factory para ejecución real</p>
          </div>
        </section>

        <section id="faq" className="border-y border-slate-800 bg-slate-900/60">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <h2 className="font-display text-3xl sm:text-4xl text-white text-center">Objeciones reales. Respuestas claras.</h2>
            <div className="mt-10 space-y-3">
              {faqs.map((item) => (
                <details key={item.q} className="group rounded-xl border border-slate-700 bg-slate-900/70 p-5">
                  <summary className="cursor-pointer font-semibold text-slate-100 list-none flex items-center justify-between">
                    {item.q}
                    <ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="mt-3 text-sm text-slate-300">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="cta" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-slate-700 bg-slate-900/80 p-8">
              <h2 className="font-display text-3xl text-white">Tu idea no necesita más espera. Necesita ejecución.</h2>
              <p className="mt-4 text-slate-300">
                Comparte tu contexto y te devolvemos una ruta de implementación clara para transformar tu idea en una
                solución SaaS funcionando.
              </p>
              <div className="mt-6 space-y-3 text-sm text-slate-300">
                <p className="flex items-center gap-2"><Handshake className="w-4 h-4 text-cyan-300" />Diagnóstico estratégico inicial</p>
                <p className="flex items-center gap-2"><Brain className="w-4 h-4 text-cyan-300" />Propuesta orientada a impacto real</p>
                <p className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-300" />Respuesta en menos de 24 h hábiles</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="/login" className="px-5 py-3 rounded-xl border border-cyan-400/40 text-cyan-200 font-semibold hover:bg-cyan-400/10 transition-colors">
                  Acceder a la suite de apps
                </a>
              </div>
            </div>

            <form onSubmit={submitLead} className="rounded-3xl border border-slate-700 bg-slate-900/80 p-8 space-y-4">
              <h3 className="text-xl font-semibold text-white">Solicita la revisión de tu idea</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Nombre completo"
                  value={lead.name}
                  onChange={(e) => updateLead('name', e.target.value)}
                  required
                />
                <input
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="email"
                  placeholder="Correo corporativo"
                  value={lead.email}
                  onChange={(e) => updateLead('email', e.target.value)}
                  required
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Empresa"
                  value={lead.company}
                  onChange={(e) => updateLead('company', e.target.value)}
                />
                <input
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Teléfono / WhatsApp"
                  value={lead.phone}
                  onChange={(e) => updateLead('phone', e.target.value)}
                />
              </div>
              <input
                className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Interés principal (inventario, rental, agenda, pagos...)"
                value={lead.interest}
                onChange={(e) => updateLead('interest', e.target.value)}
              />
              <textarea
                className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows={4}
                placeholder="Describe tu idea o el problema operativo que quieres resolver"
                value={lead.message}
                onChange={(e) => updateLead('message', e.target.value)}
                required
                minLength={15}
              />
              <input
                className="hidden"
                value={lead.website}
                onChange={(e) => updateLead('website', e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
              <div>
                <div ref={turnstileContainerRef} className="min-h-[65px]" />
                <p className="text-xs text-slate-400">Protegido por Cloudflare Turnstile</p>
              </div>

              {leadState.message && (
                <div className={`rounded-xl px-4 py-3 text-sm ${
                  leadState.type === 'success'
                    ? 'bg-emerald-500/10 border border-emerald-400/40 text-emerald-200'
                    : 'bg-rose-500/10 border border-rose-400/40 text-rose-200'
                }`}>
                  {leadState.message}
                </div>
              )}

              <button
                type="submit"
                disabled={leadSending}
                className="w-full px-5 py-3 rounded-xl font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-950 hover:from-cyan-400 hover:to-blue-400 disabled:opacity-70"
              >
                {leadSending ? 'Enviando...' : 'Solicita la revisión de tu idea'}
              </button>
              <p className="text-xs text-slate-400">Sin compromiso técnico inicial.</p>
            </form>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <p className="text-sm text-slate-400">AppEmprendedor · Infraestructura SaaS para emprendedores modernos.</p>
        </div>
      </footer>
    </div>
  )
}



